/**
 * T-G6-fe-inline (Phase-17, FR-G6) — Category combobox with inline-create.
 *
 * Compact searchable selector backed by `useCategories`; surfaces a
 * "+ Neue Kategorie anlegen…" CTA at the end of the filtered list so a
 * user mid-Beleg-entry can create a missing Kategorie without leaving
 * the form. The CTA opens a Mini-Modal with the Pflicht-Trio (name,
 * accountingClass, deductible_percent); after a successful POST via
 * `useCreateCategory` the modal closes and the new id is selected.
 *
 * Mount points (per task body): Beleg-Form, Detail-Page Inline-Edit,
 * Durcharbeitemodus, Bulk-Assign, Inline-Item-Editor. Initial wave wires
 * /expenses/new (the Beleg-Form path) — the canonical e2e mount-point.
 *
 * Selectors (Skill §2.11) — verifiable against this file:
 *   - `category-combobox` (container)
 *   - `category-combobox-search` (filter input)
 *   - `category-combobox-option-<id>` (filtered row)
 *   - `category-combobox-create-cta`
 *   - `category-combobox-create-modal`
 *   - `category-combobox-create-name` / `-class` / `-deductible`
 *   - `category-combobox-create-submit` / `-cancel`
 */
import { useState, useMemo, useRef, useEffect } from 'react';
import { Button, Input, SURFACE } from '../ui';
import { useCategories, useCategory, useCreateCategory } from '../../services/api/categories';
import { useToastHelpers } from '../ui/Toast';
import type { AccountingClass, ExpenseCategoryDto } from '@armard/shared';

interface Props {
  value: string;
  onChange: (next: string) => void;
  /** Allow the parent to refocus / close on demand. */
  testid?: string;
  placeholder?: string;
  label?: string;
}

export function CategoryComboboxWithCreate({
  value,
  onChange,
  testid,
  placeholder = '— Kategorie wählen —',
  label = 'Kategorie',
}: Props) {
  const { data, isLoading } = useCategories({ limit: 200 });
  const selectedCategoryQuery = useCategory(value || undefined);
  const create = useCreateCategory();
  const toast = useToastHelpers();

  const [search, setSearch] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);
  const wrapperRef = useRef<HTMLDivElement>(null);

  // Close the listbox on outside-click. Modal has its own dismissal
  // through the click-outside layer rendered there.
  useEffect(() => {
    function onDocClick(e: MouseEvent) {
      if (!wrapperRef.current) return;
      if (!wrapperRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    }
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, []);

  const activeCategories = useMemo(() => (data?.items ?? []).filter((c) => !c.isArchived), [data]);
  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return activeCategories;
    return activeCategories.filter((c) => c.name.toLowerCase().includes(q));
  }, [activeCategories, search]);
  const selectedCategory: ExpenseCategoryDto | undefined =
    activeCategories.find((c) => c.id === value) ??
    (selectedCategoryQuery.data?.id === value ? selectedCategoryQuery.data : undefined);

  const displayValue = selectedCategory
    ? `${selectedCategory.name}${selectedCategory.isArchived ? ' (archiviert)' : ''}`
    : value
      ? 'Kategorie wird geladen…'
      : '';

  function selectCategory(id: string) {
    onChange(id);
    setIsOpen(false);
    setSearch('');
  }

  return (
    <div ref={wrapperRef} className="relative" data-testid={testid ?? 'category-combobox'}>
      <div className="flex items-end gap-2">
        <div className="min-w-0 flex-1">
          <Input
            label={label}
            role="combobox"
            aria-autocomplete="list"
            aria-haspopup="listbox"
            aria-expanded={isOpen}
            aria-controls="category-combobox-listbox"
            aria-activedescendant={
              isOpen && filtered[activeIndex]
                ? `category-option-${filtered[activeIndex].id}`
                : undefined
            }
            value={isOpen ? search : displayValue}
            onFocus={() => {
              setIsOpen(true);
              setSearch('');
              setActiveIndex(
                Math.max(
                  0,
                  activeCategories.findIndex((c) => c.id === value)
                )
              );
            }}
            onChange={(e) => {
              setIsOpen(true);
              setSearch(e.target.value);
              setActiveIndex(0);
            }}
            onKeyDown={(event) => {
              if (event.key === 'Escape') {
                setIsOpen(false);
                setSearch('');
              } else if (event.key === 'ArrowDown') {
                event.preventDefault();
                setIsOpen(true);
                setActiveIndex((index) => Math.min(index + 1, filtered.length - 1));
              } else if (event.key === 'ArrowUp') {
                event.preventDefault();
                setIsOpen(true);
                setActiveIndex((index) => Math.max(index - 1, 0));
              } else if (event.key === 'Enter' && isOpen && filtered[activeIndex]) {
                event.preventDefault();
                selectCategory(filtered[activeIndex].id);
              }
            }}
            placeholder={placeholder}
            data-testid="category-combobox-search"
            autoComplete="off"
          />
        </div>
        {value && (
          <Button
            type="button"
            variant="ghost"
            size="sm"
            aria-label="Kategorie entfernen"
            onClick={() => {
              onChange('');
              setSearch('');
              setIsOpen(false);
            }}
          >
            Leeren
          </Button>
        )}
      </div>
      {isOpen && (
        <div
          className={`absolute z-30 mt-1 w-full max-h-72 overflow-y-auto rounded-lg ${SURFACE.solid}`}
          id="category-combobox-listbox"
          role="listbox"
          aria-label="Kategorien"
          data-testid="category-combobox-listbox"
        >
          {isLoading && <div className="px-3 py-2 text-sm text-white/50">Lade Kategorien…</div>}
          {!isLoading && filtered.length === 0 && (
            <div className="px-3 py-2 text-sm text-white/50">Keine passende Kategorie.</div>
          )}
          {filtered.map((c) => (
            <button
              key={c.id}
              type="button"
              id={`category-option-${c.id}`}
              role="option"
              aria-selected={c.id === value}
              onMouseEnter={() => setActiveIndex(filtered.findIndex((item) => item.id === c.id))}
              onClick={() => selectCategory(c.id)}
              data-testid={`category-combobox-option-${c.id}`}
              className={`w-full text-left px-3 py-2 text-sm hover:bg-white/10 ${
                filtered[activeIndex]?.id === c.id ? 'bg-white/10' : ''
              } ${c.id === value ? 'text-sky-200 bg-white/5' : 'text-white'}`}
            >
              {c.name}
              <span className="text-white/50 text-xs ml-2">({c.accountingClass})</span>
            </button>
          ))}
          {/* Spec 004 US5 (T030) FR-G6-COLLISION-GUARD — case-
              insensitive collision detection. When the typed search
              term matches an existing category's name lowercase-equal,
              surface an inline warning + a one-click "Existierende
              auswählen" affordance AND disable the create-CTA so the
              duplicate-creation path is genuinely blocked (codex
              Wave-D finding #3). */}
          {(() => {
            const typed = search.trim();
            if (!typed) return null;
            const collision = activeCategories.find(
              (c) => c.name.toLowerCase() === typed.toLowerCase()
            );
            if (!collision) return null;
            return (
              <div
                role="alert"
                data-testid="category-collision-warning"
                className="border-t border-amber-500/30 bg-amber-500/10 px-3 py-2 text-xs text-amber-100"
              >
                <span className="block mb-1.5">
                  Kategorie „{collision.name}" existiert bereits — bitte die existierende Kategorie
                  verwenden.
                </span>
                <button
                  type="button"
                  onClick={() => selectCategory(collision.id)}
                  data-testid="category-collision-select-existing"
                  className="px-2 py-1 rounded bg-amber-500/20 hover:bg-amber-500/30 text-amber-50"
                >
                  Existierende auswählen
                </button>
              </div>
            );
          })()}
          {(() => {
            const typed = search.trim();
            const collision =
              typed.length > 0 &&
              activeCategories.some((c) => c.name.toLowerCase() === typed.toLowerCase());
            return (
              <button
                type="button"
                onClick={() => {
                  if (collision) return;
                  setModalOpen(true);
                }}
                disabled={collision}
                aria-disabled={collision}
                data-testid="category-combobox-create-cta"
                className={`w-full text-left px-3 py-2 text-sm border-t border-white/10 ${
                  collision
                    ? 'text-white/40 cursor-not-allowed'
                    : 'text-emerald-200 hover:bg-emerald-500/10'
                }`}
              >
                + Neue Kategorie anlegen{search ? `: „${search}"` : '…'}
                {collision && (
                  <span className="ml-2 text-[10px] text-amber-300/80">
                    (blockiert — Duplikat-Name)
                  </span>
                )}
              </button>
            );
          })()}
        </div>
      )}
      {modalOpen && (
        <CreateCategoryModal
          presetName={search}
          submitting={create.isPending}
          onCancel={() => setModalOpen(false)}
          onCreate={async (input) => {
            try {
              const created = await create.mutateAsync(input);
              toast.success(`Kategorie „${created.name}" angelegt`);
              onChange(created.id);
              setModalOpen(false);
              setIsOpen(false);
              setSearch('');
            } catch (err) {
              toast.error(`Anlegen fehlgeschlagen: ${(err as Error).message}`);
            }
          }}
        />
      )}
    </div>
  );
}

interface CreateModalProps {
  presetName: string;
  submitting: boolean;
  onCancel: () => void;
  onCreate: (input: {
    name: string;
    accountingClass: AccountingClass;
    topic: string;
    deductiblePercent: number;
    depreciationYears: number | null;
    displayOrder: number;
  }) => void | Promise<void>;
}

function CreateCategoryModal({ presetName, submitting, onCancel, onCreate }: CreateModalProps) {
  const [name, setName] = useState(presetName);
  const [accountingClass, setAccountingClass] = useState<AccountingClass>('sammelposten');
  const [deductiblePercent, setDeductiblePercent] = useState('100');
  const [depreciationYears, setDepreciationYears] = useState('3');
  const [topic, setTopic] = useState('sonstiges');
  const normalizedDeductible = deductiblePercent.trim().replace(',', '.');
  const parsedDeductible = normalizedDeductible ? Number(normalizedDeductible) : Number.NaN;
  const deductibleError =
    Number.isFinite(parsedDeductible) && parsedDeductible >= 0 && parsedDeductible <= 100
      ? undefined
      : 'Bitte eine Zahl zwischen 0 und 100 eingeben.';
  const normalizedYears = depreciationYears.trim();
  const parsedYears = normalizedYears ? Number(normalizedYears) : Number.NaN;
  const depreciationYearsError =
    accountingClass !== 'abschreibung' || (Number.isInteger(parsedYears) && parsedYears > 0)
      ? undefined
      : 'Bitte eine positive ganze Abschreibungsdauer eingeben.';
  const submitDisabled =
    !name.trim() || !!deductibleError || !!depreciationYearsError || submitting;

  function submit() {
    if (submitDisabled) return;
    void onCreate({
      name: name.trim(),
      accountingClass,
      topic: topic.trim() || 'sonstiges',
      deductiblePercent: parsedDeductible,
      depreciationYears: accountingClass === 'abschreibung' ? parsedYears : null,
      displayOrder: 999, // append-at-end; user can re-order later in Settings
    });
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4"
      data-testid="category-combobox-create-modal"
      onClick={() => {
        if (!submitting) onCancel();
      }}
    >
      <div
        className={`rounded-xl p-6 max-w-md w-full space-y-3 ${SURFACE.solid}`}
        onClick={(e) => e.stopPropagation()}
      >
        <h3 className="text-lg text-white">Neue Kategorie anlegen</h3>
        <fieldset disabled={submitting} className="space-y-3 border-0 p-0 m-0 min-w-0">
          <Input
            label="Name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            data-testid="category-combobox-create-name"
            required
          />
          <div>
            <label className="block text-sm font-medium text-white/70 mb-1.5">
              Buchhaltungsklasse
            </label>
            <select
              value={accountingClass}
              onChange={(e) => setAccountingClass(e.target.value as AccountingClass)}
              data-testid="category-combobox-create-class"
              disabled={submitting}
              className="w-full px-4 py-2.5 rounded-lg bg-white/5 border border-white/10 text-white"
            >
              <option value="sammelposten">sammelposten</option>
              <option value="abschreibung">abschreibung</option>
            </select>
          </div>
          <Input label="Topic" value={topic} onChange={(e) => setTopic(e.target.value)} />
          <Input
            label="Abzugsfähig (%)"
            type="number"
            min="0"
            max="100"
            step="1"
            value={deductiblePercent}
            onChange={(e) => setDeductiblePercent(e.target.value)}
            error={deductibleError}
            data-testid="category-combobox-create-deductible"
          />
          {accountingClass === 'abschreibung' && (
            <Input
              label="Abschreibungsdauer (Jahre)"
              type="number"
              min="1"
              step="1"
              value={depreciationYears}
              onChange={(e) => setDepreciationYears(e.target.value)}
              error={depreciationYearsError}
              required
              data-testid="category-combobox-create-depreciation-years"
            />
          )}
        </fieldset>
        <div className="flex justify-end gap-2 pt-2">
          <Button
            type="button"
            variant="ghost"
            onClick={onCancel}
            disabled={submitting}
            data-testid="category-combobox-create-cancel"
          >
            Abbrechen
          </Button>
          <Button
            type="button"
            disabled={submitDisabled}
            data-testid="category-combobox-create-submit"
            onClick={submit}
          >
            {submitting ? 'Speichern…' : 'Anlegen'}
          </Button>
        </div>
      </div>
    </div>
  );
}
